import { emiteEventoDelEmbudo } from './analitica_del_embudo'

// Analítica de las microintervenciones contextuales (PRD-134-1, TC-134-1.4.8.6).
//
// Mide las tres cosas que pueden pasarle a una microintervención inline: que se enseñe
// (`microintervention_shown`), que la persona haga lo que propone (`microintervention_acted`) y que
// la quite de en medio (`microintervention_dismissed`). Sin los tres no se puede leer ni la eficacia
// (aceptadas / mostradas, objetivo ≥ 30 % de EPIC-134-1.6) ni la saturación (RF-20).
//
// SE ENGANCHA AL CHASIS, NO A CADA VARIANTE. Lo llama el controlador `microintervencion`
// (TC-134-1.6.2.1) al conectar, así que cualquier variante que renderice el componente nace
// instrumentada: las tres que quedan por implementar —seguir blogger (TC-134-1.6.2.4), seguir foro
// (TC-134-1.6.3.3) y re-prompt de newsletter (TC-134-1.6.4.2)— no tienen que acordarse de emitir.
//
// Mecanismo: `emiteEventoDelEmbudo` (TC-134-1.4.5.1) → `dataLayer` → GTM → GA4. No se construye
// contexto propio: las propiedades comunes —`user_id` incluido— las adjunta el helper compartido
// desde `window.rankiaAnalitica`, y la guarda de PII es la suya.
//
// LOS TRES VAN POR CLIENTE, y no es indiferente. El acto de pintar la microintervención lo conoce
// el servidor (lo anota `Onboarding::MotorDeMicrointervenciones.anota_presentacion!`), así que
// `microintervention_shown` podría salir server-side. No se hace: la eficacia es un cociente entre
// aceptadas y mostradas, y emitir el denominador por un camino (servidor: sin fugas) y el numerador
// por otro (cliente: sujeto a consentimiento y a bloqueadores) daría una tasa DEPRIMIDA por
// construcción: más impresiones contadas de las que pueden llegar a tener un clic asociado. Con los
// tres por el mismo camino, lo que se pierde se pierde en los tres y el cociente sigue siendo
// legible. El recuento sin fugas de cuántas se enseñaron ya existe aparte, en la tabla
// `microintervenciones`, que es la que gobierna la no saturación.
export const EVENTO_MOSTRADA = 'microintervention_shown'
export const EVENTO_ACEPTADA = 'microintervention_acted'
export const EVENTO_DESCARTADA = 'microintervention_dismissed'

// Espejo de `Microintervencion::TIPOS` (`app/models/microintervencion.rb`). El valor que viaja como
// `type` es EL MISMO que se guarda en la tabla, para que el dato medido y el guardado se puedan
// cruzar. La paridad entre las dos listas la verifica `spec/javascript/analitica-microintervenciones.test.js`,
// que lee el modelo: tocar una sin la otra falla ahí y no en BigQuery semanas después.
export const TIPOS = ['follow_blogger', 'follow_forum', 'newsletter_reprompt']

// Con qué control se quitó de en medio. El chasis ya los distingue (el CTA secundario envía
// `motivo=reject` y la X `motivo=dismiss`) y para la no saturación son el mismo descarte, así que
// se emiten como un solo evento con este parámetro en lugar de como dos eventos distintos.
export const DESCARTE_CTA_SECUNDARIO = 'secondary_cta'
export const DESCARTE_CIERRE = 'close'

// Qué evento del embudo emite cada evento del chasis (handoff §4).
//
// `reject` ("Ahora no") y `dismiss` (la X) son los dos un descarte: es lo que anota
// `Onboarding::MotorDeMicrointervenciones.anota_descarte!` y lo que abre la espera tras descarte
// sobre ese objeto (`ESPERA_TRAS_DESCARTE`, 60 días por defecto). Aceptar solo llega aquí cuando la
// acción primaria ha respondido bien —el chasis no confirma si falla—, así que
// `microintervention_acted` significa "aceptada Y hecha", no "pulsó el botón".
const EVENTOS_DEL_CHASIS = {
  'microintervencion:accept': { evento: EVENTO_ACEPTADA, params: {} },
  'microintervencion:reject': { evento: EVENTO_DESCARTADA, params: { dismiss_method: DESCARTE_CTA_SECUNDARIO } },
  'microintervencion:dismiss': { evento: EVENTO_DESCARTADA, params: { dismiss_method: DESCARTE_CIERRE } }
}

// Tipos ya contados como mostrados en esta página. La clave es el TIPO y no el nodo: un re-render
// (turbo stream) sustituye el elemento por otro y la misma propuesta no puede contarse dos veces.
// Dos tipos distintos en la misma página sí son dos impresiones.
const mostradas = new Set()

const manejadores = new Map()

// Arranca la instrumentación de una microintervención recién pintada y emite su impresión.
// Idempotente por tipo. La llama el controlador `microintervencion` al conectar, que es el único
// instante en el que se sabe que el bloque está en la página de verdad.
//
// Devuelve `true` si se ha llegado a emitir la impresión.
export function instrumentaLaMicrointervencion (tipo) {
  escuchaLasInteracciones()

  if (!tipo || mostradas.has(tipo)) return false
  mostradas.add(tipo)

  return emite(EVENTO_MOSTRADA, tipo)
}

// Reinicia el estado del módulo. Solo lo usan los tests: en producción la vida del módulo ES la de
// la página (Turbo Drive está desactivado en el repo — `packs/hotwired.js`).
export function reiniciaLaInstrumentacion () {
  mostradas.clear()
  manejadores.forEach((manejador, nombre) => document.removeEventListener(nombre, manejador))
  manejadores.clear()
}

// Un listener por evento del chasis, delegado en el documento y registrado UNA sola vez aunque la
// página monte varias microintervenciones. Los eventos del chasis burbujean (`dispatch` de
// Stimulus), así que no hace falta engancharse a cada elemento —ni desengancharse cuando el bloque
// se pliega y desaparece.
function escuchaLasInteracciones () {
  if (manejadores.size > 0) return

  Object.entries(EVENTOS_DEL_CHASIS).forEach(([nombre, { evento, params }]) => {
    const manejador = (dom) => emite(evento, dom.detail && dom.detail.variant, params)

    manejadores.set(nombre, manejador)
    document.addEventListener(nombre, manejador)
  })
}

// `type` es el único parámetro propio obligatorio (TC-134-1.4.8.6). `user_id` no se pone aquí: llega
// con las propiedades comunes, y está siempre porque el motor veta con `:sin_usuario` cualquier
// microintervención sin sesión.
//
// Un tipo fuera del catálogo se emite igualmente —un hueco en BigQuery no se ve; un valor raro,
// sí— y se avisa por consola, que es donde lo verá quien monte la variante. Si aparece en el dato,
// hay un host que montó el chasis con una `variante` que no es un `Microintervencion::TIPOS`.
//
// Hoy hay UN host así a propósito: la guía de estilos, cuyos ejemplos montan el chasis con
// `variante: 'blogger'`. Es la demostración del componente y sus clics no son de nadie, así que el
// valor fuera del catálogo es lo que mantiene esos eventos fuera de la medición: toda consulta del
// embudo filtra por los tres tipos del catálogo. Si algún día esos ejemplos pasaran a montar una
// variante real, harían falta un interruptor en el chasis y esta nota dejaría de valer.
function emite (evento, tipo, params = {}) {
  if (!tipo) return false

  const emitido = emiteEventoDelEmbudo(evento, { type: tipo, ...params })

  if (emitido && !TIPOS.includes(tipo)) {
    console.warn(
      `[analitica-microintervenciones] "${tipo}" no es un tipo del catálogo (${TIPOS.join(', ')}). ` +
      'El evento sale con ese valor; monta el chasis con una variante de `Microintervencion::TIPOS`.'
    )
  }

  return emitido
}
